/**
 * Map & Data Export Utilities
 */

/**
 * Export the MapLibre canvas as a PNG image.
 * @param {object} mapRef - react-map-gl map ref
 * @param {string} filename - output filename
 */
export function exportMapAsPng(mapRef, filename = 'ultimate-gis-map.png') {
  if (!mapRef?.current) return false;
  const map = mapRef.current.getMap();
  const canvas = map.getCanvas();
  const link = document.createElement('a');
  link.download = filename;
  link.href = canvas.toDataURL('image/png');
  link.click();
  return true;
}

/**
 * PNG data URL of the map canvas once the map has finished drawing (style loaded, tiles in,
 * not moving). Resolves with whatever is there after `timeout` ms rather than hanging; null if
 * there is no map. Used by the Report Builder so a snapshot taken right after load is not blank.
 */
export function captureMapDataUrl(map, { timeout = 4000 } = {}) {
  if (!map?.getCanvas) return Promise.resolve(null);
  const grab = () => { try { return map.getCanvas().toDataURL('image/png'); } catch { return null; } };
  const ready = () => map.loaded?.() !== false && !map.isMoving?.();
  if (ready()) {
    // Draw one more frame so the drawing buffer holds the settled view.
    return new Promise((resolve) => { map.once?.('render', () => resolve(grab())); map.triggerRepaint?.(); setTimeout(() => resolve(grab()), 500); });
  }
  return new Promise((resolve) => {
    let done = false;
    const finish = () => { if (done) return; done = true; clearTimeout(timer); map.off?.('idle', finish); resolve(grab()); };
    const timer = setTimeout(finish, timeout);
    map.once('idle', finish);
  });
}

/**
 * Export GeoJSON data as a downloadable file.
 * @param {object} geojson - GeoJSON FeatureCollection
 * @param {string} filename - output filename
 */
export function exportGeoJSON(geojson, filename = 'ultimate-gis-export.geojson') {
  const blob = new Blob([JSON.stringify(geojson, null, 2)], { type: 'application/json' });
  downloadBlob(blob, filename);
}

/** The first [lon, lat] of any geometry (a point's position, a line's or ring's first vertex), or null. */
export function firstLonLat(geometry) {
  let c = geometry?.coordinates;
  while (Array.isArray(c) && Array.isArray(c[0])) c = c[0];
  return Array.isArray(c) && Number.isFinite(c[0]) && Number.isFinite(c[1]) ? [c[0], c[1]] : null;
}

/** CSV text: longitude, latitude, then every property column. Rows without coordinates keep empty cells. */
export function featuresToCsv(features) {
  const keys = new Set();
  features.forEach((f) => {
    if (f.properties) Object.keys(f.properties).forEach((k) => keys.add(k));
  });
  const allKeys = ['longitude', 'latitude', ...Array.from(keys).filter((k) => k !== 'longitude' && k !== 'latitude')];
  const header = allKeys.map(escapeCSV).join(',');
  const rows = features.map((f) => {
    const p = firstLonLat(f.geometry);
    return allKeys.map((k) => {
      if (k === 'longitude') return p ? p[0] : '';
      if (k === 'latitude') return p ? p[1] : '';
      const v = f.properties?.[k];
      return escapeCSV(v !== null && typeof v === 'object' ? JSON.stringify(v) : (v ?? ''));
    }).join(',');
  });
  return [header, ...rows].join('\n');
}

/**
 * Export features as CSV.
 * @param {Array} features - GeoJSON features array
 * @param {string} filename - output filename
 * @returns {number} features written (0 = nothing exported)
 */
export function exportFeaturesAsCSV(features, filename = 'ultimate-gis-export.csv') {
  if (!features || features.length === 0) return 0;
  downloadBlob(new Blob([featuresToCsv(features)], { type: 'text/csv' }), filename);
  return features.length;
}

const hasFeatures = (gj) => Array.isArray(gj?.features) && gj.features.length > 0;
const tagged = (gj, name) => gj.features.filter((f) => f?.geometry).map((f) => ({ ...f, properties: { ...(f.properties || {}), layer: name } }));

/**
 * Every visible vector layer as one FeatureCollection; each feature gets a "layer" property
 * (the layer's name). Sources: imported files and project datasets (importedLayers), analysis
 * and drawn results (resultLayers) and the visible project data layers (layers + usgsData).
 */
export function collectVisibleFeatures({ importedLayers = [], resultLayers = [], layers = [], usgsData = {} } = {}) {
  const features = [];
  importedLayers.filter((l) => l.visible && hasFeatures(l.geojson)).forEach((l) => features.push(...tagged(l.geojson, l.name || l.id)));
  resultLayers.filter((r) => r.visible && hasFeatures(r.geojson)).forEach((r) => features.push(...tagged(r.geojson, r.name || r.id)));
  layers.filter((l) => l.visible && hasFeatures(usgsData?.[l.id])).forEach((l) => features.push(...tagged(usgsData[l.id], l.name || l.id)));
  return { type: 'FeatureCollection', features };
}

/**
 * Features for the CSV export: the active layer's (layer-tree key imp:, res: or proj:) when
 * one is active and has data; otherwise every visible point feature.
 */
export function collectCsvFeatures({ activeLayerKey = null, importedLayers = [], resultLayers = [], layers = [], usgsData = {} } = {}) {
  if (activeLayerKey) {
    const i = activeLayerKey.indexOf(':');
    const kind = activeLayerKey.slice(0, i), id = activeLayerKey.slice(i + 1);
    const src = kind === 'imp' ? importedLayers.find((l) => l.id === id)
      : kind === 'res' ? resultLayers.find((r) => r.id === id)
        : kind === 'proj' ? { name: layers.find((l) => l.id === id)?.name, geojson: usgsData?.[id] } : null;
    if (hasFeatures(src?.geojson)) return { features: src.geojson.features.filter((f) => f?.geometry), name: src.name || id };
  }
  const all = collectVisibleFeatures({ importedLayers, resultLayers, layers, usgsData });
  return { features: all.features.filter((f) => f.geometry.type === 'Point' || f.geometry.type === 'MultiPoint'), name: null };
}

/**
 * Generate a shareable URL: the current path and search params (?project=, ?dataset=, &model=)
 * plus a hash carrying the viewport and the visible layer ids.
 * @param {object} viewState - { longitude, latitude, zoom, bearing, pitch }
 * @param {Array} visibleLayerIds - array of visible layer IDs
 * @param {object} [loc] - location parts (defaults to window.location)
 * @returns {string} shareable URL
 */
export function generateShareUrl(viewState, visibleLayerIds = [], loc = window.location) {
  const params = new URLSearchParams();
  if (viewState && Number.isFinite(viewState.latitude) && Number.isFinite(viewState.longitude)) {
    params.set('lat', viewState.latitude.toFixed(5));
    params.set('lon', viewState.longitude.toFixed(5));
    if (Number.isFinite(viewState.zoom)) params.set('zoom', viewState.zoom.toFixed(2));
    if (viewState.bearing) params.set('bearing', viewState.bearing.toFixed(0));
    if (viewState.pitch) params.set('pitch', viewState.pitch.toFixed(0));
  }
  if (visibleLayerIds.length > 0) {
    params.set('layers', visibleLayerIds.join(','));
  }
  const hash = params.toString();
  return `${loc.origin}${loc.pathname}${loc.search || ''}${hash ? `#${hash}` : ''}`;
}

/**
 * Parse a share URL hash into view state (also reads the older short names z, b, p).
 * @returns {object|null} { latitude, longitude, zoom, bearing, pitch, layers }
 */
export function parseShareUrl(hashString = window.location.hash) {
  const hash = String(hashString || '').replace(/^#/, '');
  if (!hash) return null;
  const params = new URLSearchParams(hash);
  const lat = parseFloat(params.get('lat'));
  const lon = parseFloat(params.get('lon'));
  const z = parseFloat(params.get('zoom') ?? params.get('z'));
  if (isNaN(lat) || isNaN(lon) || isNaN(z)) return null;
  return {
    latitude: lat,
    longitude: lon,
    zoom: z,
    bearing: parseFloat(params.get('bearing') ?? params.get('b')) || 0,
    pitch: parseFloat(params.get('pitch') ?? params.get('p')) || 0,
    layers: params.get('layers')?.split(',').filter(Boolean) || [],
  };
}

// ── Helpers ──

function escapeCSV(value) {
  const str = String(value ?? '');
  if (str.includes(',') || str.includes('"') || str.includes('\n')) {
    return `"${str.replace(/"/g, '""')}"`;
  }
  return str;
}

const xmlEscape = (s) => String(s ?? '').replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;');
const kmlCoords = (pts) => pts.map((c) => `${c[0]},${c[1]},${c[2] ?? 0}`).join(' ');

function kmlGeometry(g) {
  if (!g) return '';
  switch (g.type) {
    case 'Point': return `<Point><coordinates>${kmlCoords([g.coordinates])}</coordinates></Point>`;
    case 'LineString': return `<LineString><coordinates>${kmlCoords(g.coordinates)}</coordinates></LineString>`;
    case 'Polygon': return `<Polygon>${g.coordinates.map((ring, i) => `<${i ? 'inner' : 'outer'}BoundaryIs><LinearRing><coordinates>${kmlCoords(ring)}</coordinates></LinearRing></${i ? 'inner' : 'outer'}BoundaryIs>`).join('')}</Polygon>`;
    case 'MultiPoint': case 'MultiLineString': case 'MultiPolygon': {
      const single = g.type.replace('Multi', '');
      return `<MultiGeometry>${g.coordinates.map((c) => kmlGeometry({ type: single, coordinates: c })).join('')}</MultiGeometry>`;
    }
    default: return '';
  }
}

/** KML document (string) for a GeoJSON FeatureCollection: one Placemark per feature, properties as ExtendedData. */
export function featureCollectionToKML(fc, name = 'Layer') {
  const marks = (fc?.features || []).map((f, i) => {
    const props = f.properties || {};
    const label = props.name ?? props.id ?? `Feature ${i + 1}`;
    const data = Object.entries(props).map(([k, v]) => `<Data name="${xmlEscape(k)}"><value>${xmlEscape(typeof v === 'object' ? JSON.stringify(v) : v)}</value></Data>`).join('');
    return `<Placemark><name>${xmlEscape(label)}</name><ExtendedData>${data}</ExtendedData>${kmlGeometry(f.geometry)}</Placemark>`;
  }).join('\n');
  return `<?xml version="1.0" encoding="UTF-8"?>\n<kml xmlns="http://www.opengis.net/kml/2.2"><Document><name>${xmlEscape(name)}</name>\n${marks}\n</Document></kml>`;
}

export function exportFeatureCollectionToKML(fc, name, filename = 'ultimate-gis-export.kml') {
  downloadBlob(new Blob([featureCollectionToKML(fc, name)], { type: 'application/vnd.google-earth.kml+xml' }), filename);
}

export function downloadBlob(blob, filename) {
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  link.click();
  URL.revokeObjectURL(url);
}

/** Copy text with the async clipboard API; resolves false (never throws) when it is unavailable or refused. */
export async function copyToClipboard(text, nav = typeof navigator !== 'undefined' ? navigator : null) {
  try {
    if (!nav?.clipboard?.writeText) return false;
    await nav.clipboard.writeText(text);
    return true;
  } catch {
    return false;
  }
}
